import { queryClient } from "@/client";
import type { PredictiveProductAnswerDto } from "@advanced-sales-technologies/backend-client/models/PredictiveProductAnswerDto";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { getAllProductsKey } from "@/hooks/api/predictive/product/useGetAllProducts";
import { getProductKey } from "@/hooks/api/predictive/product/useGetProduct";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import { useToast } from "@/hooks/useToast";

export type UpdateProductProps = {
  id: string;
  icon?: string | null;
  name: string;
  answers: PredictiveProductAnswerDto[];
  displayToast?: boolean;
};

export const useUpdateProduct = () => {
  const { apiClient } = useSalesyApiClient();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const mutation = useMutation({
    mutationFn: (requestBody: UpdateProductProps) =>
      apiClient.predictiveProducts.update({ id: requestBody.id, requestBody }),
    onSuccess: async (data, variables) => {
      if (variables.displayToast) {
        showToast({
          featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
          heading: t("predictive.product.updated.title", { name: variables.name }),
        });
      }

      await queryClient.invalidateQueries({ queryKey: getAllProductsKey });
      await queryClient.invalidateQueries({ queryKey: getProductKey(data.id) });
    },
  });

  return {
    ...mutation,
    updateProduct: mutation.mutate,
  };
};
